/**
 * Hook to track progress of Subject Management AI requests using Server-Sent Events
 *
 * Monitors real-time progress updates from the backend AI processing pipeline.
 * Shows stage names and elapsed time for two-phase LLM processing.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentProject } from '../store/slices/user';
import { openEventSource } from "../api/event-source";

// Stage names for progress display
const STAGE_NAMES = {
  started: 'Starting...',
  determining_context: 'Analyzing question...',
  fetching_data: 'Fetching subject data...',
  building_context: 'Building context...',
  sending_to_llm: 'Sending to AI...',
  waiting_for_llm: 'AI is analyzing...',
  received_from_llm: 'Received response...',
  parsing_response: 'Processing response...',
  storing_response: 'Saving results...',
  completed: 'Completed!',
  error: 'Error occurred'
};

export function useSubjectManagementProgressTracking(requestId, baseUrl = null) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const updateQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const currentProject = useSelector(selectCurrentProject);

  const processUpdateQueue = useCallback(() => {
    if (isProcessingQueueRef.current || updateQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    const processNext = () => {
      if (updateQueueRef.current.length === 0) {
        isProcessingQueueRef.current = false;
        return;
      }

      const nextUpdate = updateQueueRef.current.shift();
      setProgress(nextUpdate);

      const delay = nextUpdate.isHistorical ? 300 : 100;
      setTimeout(processNext, delay);
    };

    processNext();
  }, []);

  useEffect(() => {
    if (!requestId) {
      setProgress(null);
      setError(null);
      setIsConnected(false);
      updateQueueRef.current = [];
      isProcessingQueueRef.current = false;
      return;
    }

    // LF360-1970: the session cookie carries this stream now, so an
    // administrator's JWT no longer travels in a URL. `openEventSource`
    // still appends a token left over from before the cookie release.
    const eventSource = openEventSource(
      `/api/v1/reports-ai/progress/${requestId}/stream`,
      { projectId: currentProject?.id },
      baseUrl,
    );
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        updateQueueRef.current.push(data);
        processUpdateQueue();
        setError(null);
      } catch (err) {
        console.error('[SubjectManagementProgressTracking] Failed to parse progress data:', err);
        setError('Failed to parse progress data');
      }
    };

    eventSource.addEventListener('done', (event) => {
      try {
        const data = JSON.parse(event.data);
        updateQueueRef.current.push(data);
        processUpdateQueue();
        eventSource.close();

        const queueLength = updateQueueRef.current.length;
        const totalDelay = (queueLength * 300) + 2000;
        setTimeout(() => {
          setProgress(null);
        }, totalDelay);
      } catch (err) {
        console.error('[SubjectManagementProgressTracking] Failed to parse done event:', err);
      }
    });

    eventSource.onerror = () => {
      // Check if we have progress before setting error
      // Using ref to avoid dependency on progress state
      if (updateQueueRef.current.length === 0) {
        setError('Failed to connect to progress stream');
      }
      eventSource.close();
    };

    return () => {
      if (eventSource.readyState !== EventSource.CLOSED) {
        eventSource.close();
      }
      updateQueueRef.current = [];
      isProcessingQueueRef.current = false;
    };
  }, [requestId, baseUrl, currentProject, processUpdateQueue]);

  const stageName = (progress && progress.stage)
    ? (STAGE_NAMES[progress.stage] || progress.stage)
    : 'Initializing...';

  return {
    progress,
    error,
    stageName,
    stage: (progress && progress.stage) || null,
    elapsed: (progress && progress.elapsed) || 0,
    details: (progress && progress.details) || {},
    isConnected
  };
}

export default useSubjectManagementProgressTracking;
